import React, {useCallback} from 'react';

import {BulkActionDownload, BulkActionResultModal} from 'src/components/bulk-tool';
import {CompleteMark} from 'src/components/complete-mark';
import {AccountLink, UserLink} from 'src/components/links';
import {RowNumbered} from 'src/components/row-numbered';
import {useAccountManagerTypes} from 'src/hooks/dictionaries';
import {getBulkResult, getUpdateRelationsProgress} from 'src/openapi/admin/apis/AccountManagerBulkApi';
import {useAppDispatch} from 'src/store/hooks';

import {EllipsisTextHint} from '@pb/kit-component-ellipsis-text-hint';
import {Group, Item} from '@pb/kit-component-group';
import {Icon, Icons} from '@pb/kit-component-icon';
import {Body, Cell, Head, Row, Table} from '@pb/kit-component-table';
import {pluralize} from '@pb/kit-formatters';

import {FILE_HEADERS, FILE_NAME, TOOL_TITLE} from './constants';

import type {Pagination} from 'src/openapi/admin/models/Pagination';
import type {ProcessedUpdateRelationItem} from 'src/openapi/admin/models/ProcessedUpdateRelationItem';

type Props = {
    id: string,
    onClose: () => void,
};

export const UpdateAccountTeamMemberResultModal = ({id, onClose}: Props) => {
    const apiDispatch = useAppDispatch();
    const {getAccountManagerTypeByCode} = useAccountManagerTypes();

    const getPaginatedResults = useCallback(
        (bulkResult: ProcessedUpdateRelationItem[], {currentPage, pageSize}: Pagination) => bulkResult
            .slice((currentPage - 1) * pageSize, currentPage * pageSize)
            .map((res: ProcessedUpdateRelationItem, index) => {
                const accountLink = (
                    <AccountLink
                        accountId={res.accountId || 0}
                        accountName={res.accountName}
                        blank={true}
                        place={`${TOOL_TITLE} Result`}
                    />
                );

                const role = res.typeCode
                    ? getAccountManagerTypeByCode(res.typeCode)?.description
                    : '--';

                return (
                    <Row key={index}>
                        <Cell width={50}>
                            <RowNumbered
                                maxRowNumber={bulkResult?.length || 0}
                                placeholder={(currentPage - 1) * pageSize + index + 1}
                            />
                        </Cell>
                        <Cell>{accountLink}</Cell>
                        <Cell>
                            <EllipsisTextHint title={role}>
                                {role}
                            </EllipsisTextHint>
                        </Cell>
                        <Cell className="text-align_center">
                            {res.primary ? <Icon type={Icons.CHECK}/> : null}
                        </Cell>
                        <Cell className="text-align_center">
                            {res.receiveAlert ? <Icon type={Icons.CHECK}/> : null}
                        </Cell>
                        <Cell>
                            <CompleteMark errors={res.errors} tooltip={res.affectedRelations}/>
                        </Cell>
                    </Row>
                );
            }),
        [getAccountManagerTypeByCode],
    );

    const getLink = useCallback((itemList: Array<ProcessedUpdateRelationItem>) => (
        <UserLink
            blank={true}
            place={`${TOOL_TITLE} Result`}
            userId={itemList[0].managerId}
            caption={itemList[0].managerEmail}
        />
    ), []);

    return (
        <BulkActionResultModal<ProcessedUpdateRelationItem>
            id={id}
            onClose={onClose}
            width={1200}

            toolTitle={TOOL_TITLE}
            description={bulkResult => (
                <Group justify="space-between" alignItems="center">
                    {bulkResult && bulkResult.itemList && bulkResult.itemList.length > 0 ? (
                        <Item>
                            Result of the assignment of {getLink(bulkResult.itemList)} to {
                                pluralize('account[s]', bulkResult?.itemList?.length)
                            }
                        </Item>
                    ) : null}
                    <BulkActionDownload
                        toolName={TOOL_TITLE}
                        fileName={FILE_NAME}
                        headers={FILE_HEADERS}
                        startTime={bulkResult?.startTime}
                        body={bulkResult.itemList?.map(item => ({
                            accountName: item.accountName,
                            managerEmail: item.managerEmail,
                            newRole: item.typeCode ? getAccountManagerTypeByCode(item.typeCode)?.description : 'None',
                            primary: item.primary ? 'Yes' : 'No',
                            receiveAlert: item.receiveAlert ? 'Yes' : 'No',
                            errors: item.errors?.join('. '),
                            actionStatus: item.actionStatus,
                            affectedRelations: item.affectedRelations?.join('. '),
                        })) as Record<string, string>[]}
                    />
                </Group>
            )}
            table={(result, pagination) => (
                <Table borderedGrid={true}>
                    <Head>
                        <Cell width={56}><strong>#</strong></Cell>
                        <Cell width={200}>Account</Cell>
                        <Cell width={200}>Account Team Role</Cell>
                        <Cell className="text-align_center" width={150}>Primary</Cell>
                        <Cell width={180} className="text-align_center">Receive Account Alerts</Cell>
                        <Cell width={180}>Action Status</Cell>
                    </Head>
                    <Body>
                        {getPaginatedResults(result, pagination)}
                    </Body>
                </Table>
            )}

            getProgress={() => apiDispatch(getUpdateRelationsProgress({id}))}
            getResult={() => apiDispatch(getBulkResult({id}))}

            sortComparator={(a, b) => a.accountName.localeCompare(b.accountName)}
        />
    );
};
